import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, RouteComponentProps, Route, RouteProps } from 'react-router-dom';
import { handleError } from 'lib/helpers/error-handler';
import { getAuthorizationUrl, fetchAuthorizationUrl, isAuthorized, isTokenExpired, flush } from 'lib/auth';
import { redirectToUrl } from 'lib/helpers/redirect';
import messages from 'constants/messages';
import routes from 'router/routes';
import { IStoreState } from 'store';

type ProtectedRouteProps = RouteProps & {
  component: React.ComponentType<RouteComponentProps<ReactNode>> | React.ComponentType<ReactNode>;
};

function ProtectedRoute({ component: Component, ...rest }: ProtectedRouteProps) {
  const [loading, setLoading] = useState(true);
  const [authorizationUrl, setAuthorizationUrl] = useState<string | undefined>(undefined);
  const isAppReady = useSelector((state: IStoreState) => state.general.isAppReady);

  useEffect(() => {
    const authUrl = getAuthorizationUrl();

    if (authUrl) {
      setAuthorizationUrl(authUrl);
      setLoading(false);
    } else {
      fetchAuthorizationUrl()
        .then((a: string) => {
          setAuthorizationUrl(a);
        })
        .catch((error: Error) => {
          handleError(error.message || messages.errors.authorizationUrl);
        })
        .finally(() => setLoading(false));
    }
  }, [rest.location]);

  if (isAuthorized()) {
    if (isTokenExpired()) {
      flush();
      return <Redirect to={routes.dashboard} />;
    }

    if (isAppReady) {
      return <Route {...rest} component={Component} />;
    }

    return null;
  }

  if (loading) {
    return null;
  }

  if (authorizationUrl) {
    redirectToUrl(authorizationUrl);
    return null;
  }

  return <Redirect to={routes.dashboard} />;
}

export default ProtectedRoute;
